import React, {
  createContext,
  useContext,
  useReducer,
  useRef,
  useCallback,
  ReactNode,
} from "react";
import { IItemPosition, ISizeInfo } from "./types";

// 定义状态和动作类型
interface LazyListState {
  root: HTMLDivElement | null;
  itemPositionMap: { [key: number]: IItemPosition };
  visibleIndices: Set<number>;
}

interface UpdateVisibleIndicesAction {
  type: "updateVisibleIndices";
  payload: Set<number>;
}

interface UpdateItemPositionMap {
  type: "updateItemPosition";
  payload: { index: number; size: ISizeInfo };
}

export type LazyListAction = UpdateVisibleIndicesAction | UpdateItemPositionMap;

// 定义 reducer 函数
export const lazyListReducer = (
  state: LazyListState,
  action: LazyListAction
): LazyListState => {
  switch (action.type) {
    case "updateVisibleIndices":
      return {
        ...state,
        visibleIndices: action.payload,
      };
    case "updateItemPosition": {
      const { index, size } = action.payload;
      const newHeight = size.height;
      const itemPositionMap = { ...state.itemPositionMap };

      if (!itemPositionMap[index]) {
        itemPositionMap[index] = {
          top: Object.values(itemPositionMap)
            .slice(0, index)
            .reduce((sum, item) => sum + item.height, 0),
          height: newHeight,
        };
      }
      Object.values(itemPositionMap).forEach((_position, idx) => {
        if (idx < index || !itemPositionMap[idx]) return;
        if (idx === index) {
          itemPositionMap[idx].height = newHeight;
        }
        itemPositionMap[idx].top = Object.values(itemPositionMap)
          .slice(0, idx)
          .reduce((sum, item) => sum + item.height, 0);
      });

      return {
        ...state,
        itemPositionMap,
      };
    }
    default:
      return state;
  }
};

// 定义初始状态
export const initialState: LazyListState = {
  root: null,
  itemPositionMap: {},
  visibleIndices: new Set(),
};

// 定义上下文类型
export interface LazyListContextType {
  state: LazyListState;
  dispatch: (action: LazyListAction) => void;
  visibleIndicesRef: React.MutableRefObject<Set<number>>;
}

// 创建上下文
const LazyListContext = createContext<LazyListContextType | undefined>(
  undefined
);

// 创建自定义 Hook 以便在组件中使用上下文
export const useLazyListContext = () => {
  const contextValue = useContext(LazyListContext);
  if (!contextValue) {
    throw new Error(
      "useLazyListContext must be used within a LazyListProvider"
    );
  }
  return contextValue;
};

// 创建上下文提供者组件
export const LazyListProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(lazyListReducer, initialState);
  const visibleIndicesRef = useRef<Set<number>>(new Set());

  const enhancedDispatch = useCallback(
    (action: LazyListAction) => {
      if (action.type === "updateVisibleIndices") {
        visibleIndicesRef.current = action.payload;
      }
      dispatch(action);
    },
    [dispatch]
  );

  return (
    <LazyListContext.Provider
      value={{ state, dispatch: enhancedDispatch, visibleIndicesRef }}
    >
      {children}
    </LazyListContext.Provider>
  );
};
